import { useState } from "react";
import {
  Button,
  Group,
  Stack,
  Text,
  Card,
  ActionIcon,
  Tooltip,
  Loader,
  Center,
  Badge,
  Box,
  Divider,
} from "@mantine/core";
import { IconTrash, IconPlus, IconNote } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { casesService, CaseNote } from "../../services/cases.service";
import RichTextDiagnosis from "./RichTextDiagnosis";

interface NotesCenterProps {
  caseId: string | null;
}

const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

export function NotesCenter({ caseId }: NotesCenterProps) {
  const [newNote, setNewNote] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const queryClient = useQueryClient();

  const { data: notes = [], isLoading } = useQuery({
    queryKey: ["caseNotes", caseId],
    queryFn: () =>
      caseId ? casesService.getNotes(caseId) : Promise.resolve([]),
    enabled: !!caseId,
  });

  const createNoteMutation = useMutation({
    mutationFn: (noteText: string) =>
      casesService.createNote(caseId!, noteText),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["caseNotes", caseId] });
      setNewNote("");
      setIsAdding(false);
      notifications.show({
        title: "Note Added",
        message: "Case note has been saved",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to add note",
        color: "red",
      });
    },
  });

  const deleteNoteMutation = useMutation({
    mutationFn: (noteId: string) => casesService.deleteNote(caseId!, noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["caseNotes", caseId] });
      notifications.show({
        title: "Note Deleted",
        message: "Case note has been removed",
        color: "blue",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to delete note",
        color: "red",
      });
    },
  });

  const handleAddNote = () => {
    const stripped = newNote.replace(/<[^>]*>/g, "").trim();
    if (stripped) {
      createNoteMutation.mutate(newNote);
    }
  };

  if (!caseId) {
    return (
      <Box>
        <Text c="dimmed" ta="center" size="sm" py="xl">
          Save the case first to add notes
        </Text>
      </Box>
    );
  }

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between" align="center">
        <Group gap="xs">
          <IconNote size={20} color="var(--mantine-color-blue-6)" />
          <Text size="lg" fw={600}>
            Notes
          </Text>
          {notes.length > 0 && (
            <Badge size="sm" variant="light" color="blue">
              {notes.length}
            </Badge>
          )}
        </Group>
        {!isAdding && (
          <Button
            size="sm"
            leftSection={<IconPlus size={14} />}
            onClick={() => setIsAdding(true)}
          >
            Add Note
          </Button>
        )}
      </Group>

      <Divider />

      {/* Add note form */}
      {isAdding && (
        <Stack gap="sm">
          <RichTextDiagnosis
            value={newNote}
            onChange={setNewNote}
            placeholder="Enter note..."
            minHeight={120}
          />
          <Group justify="flex-end" gap="xs">
            <Button
              size="sm"
              variant="subtle"
              onClick={() => {
                setIsAdding(false);
                setNewNote("");
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleAddNote}
              loading={createNoteMutation.isPending}
              disabled={!newNote.replace(/<[^>]*>/g, "").trim()}
            >
              Save Note
            </Button>
          </Group>
        </Stack>
      )}

      {/* Notes list */}
      {isLoading ? (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      ) : notes.length === 0 ? (
        <Center py="xl">
          <Stack align="center" gap="xs">
            <IconNote size={40} color="var(--mantine-color-gray-4)" />
            <Text c="dimmed" size="sm">
              No notes yet. Add the first one.
            </Text>
          </Stack>
        </Center>
      ) : (
        <Stack gap="md">
          {notes.map((note: CaseNote) => (
            <Card key={note.id} withBorder p="md" shadow="xs">
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <Stack gap={8} style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{ wordBreak: "break-word" }}
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: note.noteText }}
                  />
                  <Text size="xs" c="dimmed">
                    {note.createdBy?.fullName || "Unknown"} •{" "}
                    {formatDate(note.createdAt)}
                  </Text>
                </Stack>
                <Tooltip label="Delete note">
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    size="sm"
                    onClick={() => deleteNoteMutation.mutate(note.id)}
                    loading={deleteNoteMutation.isPending}
                  >
                    <IconTrash size={14} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Card>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
